import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class ImageZoomService {

  private globalClickListener: any;
  private globalScrollListener: any;
  private visibilityChangeListener: any;
  private currentImageConfig: { width: number, height: number, border: string, round: boolean } | null = null;
  private currentImageElement: HTMLImageElement | null = null;

  constructor() {
    this.setupGlobalListeners();
  }

  /**
   * Setup global listeners to hide zoom lens on scroll
   */
  private setupGlobalListeners(): void {
    // Hide zoom lens on scroll
    this.globalScrollListener = () => {
      this.hideZoomLens();
    };
    window.addEventListener('scroll', this.globalScrollListener, true);

    // Hide zoom lens on ANY click anywhere on the page
    this.globalClickListener = (event: MouseEvent) => {
      const zoomLens = document.getElementById('BlowupLens');
      
      // Hide zoom lens on ANY click if it's currently visible
      if (zoomLens && zoomLens.style.display !== 'none') {
        this.hideZoomLens();
        
        // Restore cursor for all images
        const displayImage = document.getElementById('displayImage') as HTMLImageElement;
        if (displayImage) {
          displayImage.style.cursor = 'default';
        }
      }
    };
    document.addEventListener('click', this.globalClickListener);
    // Also listen on mousedown for faster response
    document.addEventListener('mousedown', this.globalClickListener);
    
    // Hide zoom lens when page visibility changes (tab switch, minimize, etc.)
    this.visibilityChangeListener = () => {
      if (document.hidden) {
        this.hideZoomLens();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityChangeListener);
  }

  /**
   * Initialize image zoom functionality (replacement for jQuery blowup)
   */
  initImageZoom(displayImageId: string, config: { width: number, height: number, border: string, round: boolean }): void {
    const displayImage = document.getElementById(displayImageId) as HTMLImageElement;
    if (!displayImage) return;

    this.createZoomLens(config);
    
    // Store config and image reference for re-showing zoom after click
    this.currentImageConfig = config;
    this.currentImageElement = displayImage;

    // Remove existing event listeners to avoid duplicates
    const clonedImage = displayImage.cloneNode(true) as HTMLImageElement;
    if (displayImage.parentNode) {
      displayImage.parentNode.replaceChild(clonedImage, displayImage);
    }
    
    // Update reference to cloned image
    this.currentImageElement = clonedImage;

    // Add new event listeners
    clonedImage.addEventListener('mouseenter', (e) => this.handleMouseEnter(e, config));
    clonedImage.addEventListener('mousemove', (e) => this.handleMouseMove(e, config));
    clonedImage.addEventListener('mouseleave', () => this.handleMouseLeave());
  }

  /**
   * Create the zoom lens element
   */
  private createZoomLens(config: { width: number, height: number, border: string, round: boolean }): void {
    let zoomLens = document.getElementById('BlowupLens');
    if (!zoomLens) {
      zoomLens = document.createElement('div');
      zoomLens.id = 'BlowupLens';
      document.body.appendChild(zoomLens);
    }
    zoomLens.style.cssText = `
      position: absolute;
      width: ${config.width}px;
      height: ${config.height}px;
      border: ${config.border};
      border-radius: ${config.round ? '50%' : 'none'};
      background-repeat: no-repeat;
      display: none;
      pointer-events: none;
      z-index: 999999;
    `;
  }

  /**
   * Handle mouse enter event
   */
  private handleMouseEnter(event: MouseEvent, config: { width: number, height: number, border: string, round: boolean }): void {
    const zoomLens = document.getElementById('BlowupLens');
    const image = event.target as HTMLImageElement;

    if (zoomLens && image) {
      // Change cursor to crosshair
      image.style.cursor = 'crosshair';
      
      zoomLens.style.display = 'block';
      zoomLens.style.backgroundImage = `url(${image.src})`;
      zoomLens.style.backgroundSize = `${image.naturalWidth}px ${image.naturalHeight}px`;
      this.handleMouseMove(event, config);
    }
  }

  /**
   * Handle mouse move event
   */
  private handleMouseMove(event: MouseEvent, config: { width: number, height: number, border: string, round: boolean }): void {
    const zoomLens = document.getElementById('BlowupLens');
    const image = event.target as HTMLImageElement;

    if (zoomLens && image) {
      // If zoom lens is hidden but mouse is still on image, re-show it
      if (zoomLens.style.display === 'none') {
        image.style.cursor = 'crosshair';
        zoomLens.style.display = 'block';
        zoomLens.style.backgroundImage = `url(${image.src})`;
        zoomLens.style.backgroundSize = `${image.naturalWidth}px ${image.naturalHeight}px`;
      }
      
      const rect = image.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      const xPercent = (x / rect.width) * 100;
      const yPercent = (y / rect.height) * 100;

      // Center the lens on the mouse cursor by subtracting half the lens dimensions
      const lensWidth = parseInt(zoomLens.style.width) || 400;
      const lensHeight = parseInt(zoomLens.style.height) || 400;
      
      zoomLens.style.left = `${event.clientX - (lensWidth / 2)}px`;
      zoomLens.style.top = `${event.clientY - (lensHeight / 2)}px`;
      zoomLens.style.backgroundPosition = `${xPercent}% ${yPercent}%`;
    }
  }

  /**
   * Handle mouse leave event
   */
  private handleMouseLeave(): void {
    const zoomLens = document.getElementById('BlowupLens');
    if (zoomLens) {
      zoomLens.style.display = 'none';
    }
    
    // Restore cursor to default
    const displayImage = document.getElementById('displayImage') as HTMLImageElement;
    if (displayImage) {
      displayImage.style.cursor = 'default';
    }
  }

  /**
   * Hide zoom lens (replacement for jQuery hide())
   */
  hideZoomLens(): void {
    const zoomLens = document.getElementById('BlowupLens');
    if (zoomLens) {
      zoomLens.style.display = 'none';
    }
    
    // Restore cursor to default for all images
    const displayImage = document.getElementById('displayImage') as HTMLImageElement;
    if (displayImage) {
      displayImage.style.cursor = 'default';
    }
  }

  /**
   * Show zoom lens (replacement for jQuery show())
   */
  showZoomLens(): void {
    const zoomLens = document.getElementById('BlowupLens');
    if (zoomLens) {
      zoomLens.style.display = 'block';
    }
  }

  /**
   * Force cleanup of zoom lens (call this in ngOnDestroy)
   */
  cleanup(): void {
    this.hideZoomLens();
    
    // Remove any active zoom lens element
    const zoomLens = document.getElementById('BlowupLens');
    if (zoomLens && zoomLens.style.display !== 'none') {
      zoomLens.style.display = 'none';
    }
  }

  /**
   * Get screen breakpoint (replacement for jQuery window width detection)
   */
  getScreenBreakpoint(breakpoint: number = 1040): boolean {
    return window.innerWidth <= breakpoint;
  }
}